import React, { useState, useEffect } from 'react';
import {
  FileCheck,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectDialog, setRejectDialog] = useState({ open: false, quoteId: null });
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const res = await axios.get(`${API_URL}/api/portal/quotations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotations(res.data);
    } catch (err) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const acceptQuotation = async (id) => {
    if (!window.confirm('Accept this quotation?')) return;
    try {
      const token = localStorage.getItem('portal_token');
      await axios.post(`${API_URL}/api/portal/quotations/${id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation accepted!');
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const rejectQuotation = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      await axios.post(`${API_URL}/api/portal/quotations/${rejectDialog.quoteId}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation rejected');
      setRejectDialog({ open: false, quoteId: null });
      setRejectReason('');
      fetchQuotations();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-blue-100 text-blue-700',
      viewed: 'bg-purple-100 text-purple-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-amber-100 text-amber-700',
      converted: 'bg-indigo-100 text-indigo-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const pendingQuotes = quotations.filter(q => ['sent', 'viewed'].includes(q.status));
  const totalValue = pendingQuotes.reduce((sum, q) => sum + (q.total || 0), 0);

  const filteredQuotations = quotations.filter(q =>
    q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Quotations</h1>
        <p className="text-muted-foreground">Review and respond to quotations</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FileCheck className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{quotations.length}</div>
                <div className="text-xs text-muted-foreground">Total Quotations</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{pendingQuotes.length}</div>
                <div className="text-xs text-muted-foreground">Pending Response</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">${totalValue.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">Pending Value</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search quotations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Quotations List */}
      <div className="space-y-4">
        {filteredQuotations.map((quote) => (
          <Card key={quote.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg">{quote.quote_number}</h3>
                    {getStatusBadge(quote.status)}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Received on {new Date(quote.created_at).toLocaleDateString()}
                    {quote.valid_until && ` • Valid until ${new Date(quote.valid_until).toLocaleDateString()}`}
                  </p>
                  
                  {/* Items preview */}
                  <div className="bg-muted/50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium mb-2">Items:</p>
                    {quote.items?.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.description}</span>
                        <span className="text-muted-foreground">{item.quantity} × ${item.unit_price}</span>
                      </div>
                    ))}
                    {quote.items?.length > 3 && (
                      <p className="text-xs text-muted-foreground mt-2">+{quote.items.length - 3} more items</p>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-3xl font-bold">${quote.total?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                </div>
              </div>
              
              {['sent', 'viewed'].includes(quote.status) && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setRejectDialog({ open: true, quoteId: quote.id })}
                  >
                    <XCircle className="h-4 w-4 mr-2" /> Reject
                  </Button>
                  <Button onClick={() => acceptQuotation(quote.id)}>
                    <CheckCircle className="h-4 w-4 mr-2" /> Accept Quotation
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {filteredQuotations.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No quotations found
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Quotation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please let us know why you're rejecting this quotation..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, quoteId: null })}>Cancel</Button>
            <Button variant="destructive" onClick={rejectQuotation}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalQuotations;
